import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    Tab,
    Tabs,
    TextField,
    MenuItem,
    IconButton,
    useTheme,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    FormControl,
    InputLabel,
    Select,
    Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
    Download,
    FileDownload,
    PictureAsPdf,
    TableChart,
    Assessment,
    Business,
    LocalShipping,
    MonetizationOn,
    DateRange,
    Print,
    Refresh,
    Visibility,
    Receipt,
    TrendingUp,
    Timeline,
    Analytics,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { getFreightRecords, FreightRecord } from '../api/handleFreightRecords';
import { getAllCompanies, Company } from '../api/handleCompanies';
import { useAccount } from '../contexts/AccountContext';

interface ReportData {
    totalRevenue: number;
    totalFreightCosts: number;
    totalTonnage: number;
    totalRecords: number;
    avgRevenuePerTon: number;
    paidRecords: number;
    unpaidRecords: number;
    recordsByMonth: Array<{
        month: string;
        revenue: number;
        tonnage: number;
        recordCount: number;
    }>;
    topCarriers: Array<{
        name: string;
        revenue: number;
        tonnage: number;
        recordCount: number;
    }>;
    topSuppliers: Array<{
        name: string;
        revenue: number;
        tonnage: number;
        recordCount: number;
    }>;
    carriersByVolume: Array<{
        name: string;
        tonnage: number;
        recordCount: number;
        avgTonnagePerLoad: number;
    }>;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

const TabPanel = (props: TabPanelProps) => {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`reports-tabpanel-${index}`}
            aria-labelledby={`reports-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
};

const Reports = () => {
    const theme = useTheme();
    const { currentAccount, isImpersonating, impersonatedUser } = useAccount();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [tabValue, setTabValue] = useState(0);
    const [exportDialogOpen, setExportDialogOpen] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [selectedReportType, setSelectedReportType] = useState('financial');

    // Date filtering
    const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().subtract(3, 'months'));
    const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs());

    // Data states
    const [records, setRecords] = useState<FreightRecord[]>([]);
    const [companies, setCompanies] = useState<Company[]>([]);
    const [filteredRecords, setFilteredRecords] = useState<FreightRecord[]>([]);
    const [reportData, setReportData] = useState<ReportData | null>(null);

    // Table pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const fetchData = async () => {
        if (!currentAccount) return;

        try {
            setLoading(true);
            setError(null);

            const impersonationData = isImpersonating && impersonatedUser ? {
                isImpersonating: true,
                impersonatedUserId: impersonatedUser.id,
                accountId: currentAccount.account_id,
            } : undefined;

            const [recordsData, companiesData] = await Promise.all([
                getFreightRecords(impersonationData),
                getAllCompanies(impersonationData),
            ]);

            setRecords(recordsData);
            setCompanies(companiesData);
        } catch (err: any) {
            setError(err.message || 'Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const filterRecordsByDate = (records: FreightRecord[]) => {
        if (!dateFrom || !dateTo) return records;

        return records.filter(record => {
            const recordDate = record.deliveryDate ? dayjs(record.deliveryDate) : dayjs(record.createdAt);
            return recordDate.isAfter(dateFrom.subtract(1, 'day')) && recordDate.isBefore(dateTo.add(1, 'day'));
        });
    };

    const generateReportData = (records: FreightRecord[]) => {
        const totalRevenue = records.reduce((sum, r) => sum + (r.grossPrice || 0), 0);
        const totalFreightCosts = records.reduce((sum, r) => sum + (r.freightCost || 0), 0);
        const totalTonnage = records.reduce((sum, r) => sum + (r.netTons || 0), 0);

        // Monthly data
        const monthlyMap = new Map();
        records.forEach(record => {
            const date = record.deliveryDate ? new Date(record.deliveryDate) : new Date(record.createdAt || '');
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            const monthLabel = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });

            if (!monthlyMap.has(monthKey)) {
                monthlyMap.set(monthKey, { month: monthLabel, revenue: 0, tonnage: 0, recordCount: 0 });
            }

            const monthData = monthlyMap.get(monthKey);
            monthData.revenue += record.grossPrice || 0;
            monthData.tonnage += record.netTons || 0;
            monthData.recordCount += 1;
        });

        // Top performers
        const carrierMap = new Map();
        const supplierMap = new Map();

        records.forEach(record => {
            if (record.carrier) {
                if (!carrierMap.has(record.carrier)) {
                    carrierMap.set(record.carrier, { name: record.carrier, revenue: 0, tonnage: 0, recordCount: 0 });
                }
                const carrierData = carrierMap.get(record.carrier);
                carrierData.revenue += record.grossPrice || 0;
                carrierData.tonnage += record.netTons || 0;
                carrierData.recordCount += 1;
            }

            if (record.supplier) {
                if (!supplierMap.has(record.supplier)) {
                    supplierMap.set(record.supplier, { name: record.supplier, revenue: 0, tonnage: 0, recordCount: 0 });
                }
                const supplierData = supplierMap.get(record.supplier);
                supplierData.revenue += record.grossPrice || 0;
                supplierData.tonnage += record.netTons || 0;
                supplierData.recordCount += 1;
            }
        });

        // Create carrier volume data
        const carriersByVolume = Array.from(carrierMap.values()).map(carrier => ({
            name: carrier.name,
            tonnage: carrier.tonnage,
            recordCount: carrier.recordCount,
            avgTonnagePerLoad: carrier.recordCount > 0 ? carrier.tonnage / carrier.recordCount : 0,
        })).sort((a, b) => b.tonnage - a.tonnage);

        const reportData: ReportData = {
            totalRevenue,
            totalFreightCosts,
            totalTonnage,
            totalRecords: records.length,
            avgRevenuePerTon: totalTonnage > 0 ? totalRevenue / totalTonnage : 0,
            paidRecords: records.filter(r => r.paidStatus).length,
            unpaidRecords: records.filter(r => !r.paidStatus).length,
            recordsByMonth: Array.from(monthlyMap.values()).sort((a, b) => new Date(a.month + ' 1').getTime() - new Date(b.month + ' 1').getTime()),
            topCarriers: Array.from(carrierMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 15),
            topSuppliers: Array.from(supplierMap.values()).sort((a, b) => b.revenue - a.revenue).slice(0, 15),
            carriersByVolume: carriersByVolume.slice(0, 15),
        };

        setReportData(reportData);
    };

    useEffect(() => {
        fetchData();
    }, [currentAccount, isImpersonating, impersonatedUser]);

    useEffect(() => {
        const filtered = filterRecordsByDate(records);
        setFilteredRecords(filtered);
        generateReportData(filtered);
    }, [records, dateFrom, dateTo]);

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatTonnage = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(value);
    };

    const exportToCSV = (data: any[], filename: string) => {
        if (data.length === 0) return;

        const headers = Object.keys(data[0]);
        const csvContent = [
            headers.join(','),
            ...data.map(row =>
                headers.map(header => {
                    const value = row[header];
                    return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
                }).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `${filename}.csv`;
        link.click();
    };

    const exportCurrentReport = (format: 'csv' | 'pdf') => {
        const timestamp = new Date().toISOString().split('T')[0];

        if (format === 'csv') {
            if (selectedReportType === 'financial') {
                const summaryData = [{
                    'Total Revenue': reportData?.totalRevenue || 0,
                    'Total Freight Costs': reportData?.totalFreightCosts || 0,
                    'Total Tonnage': reportData?.totalTonnage || 0,
                    'Total Records': reportData?.totalRecords || 0,
                    'Average Revenue per Ton': reportData?.avgRevenuePerTon || 0,
                    'Paid Records': reportData?.paidRecords || 0,
                    'Unpaid Records': reportData?.unpaidRecords || 0,
                }];
                exportToCSV(summaryData, `financial-summary-${timestamp}`);
            } else if (selectedReportType === 'detailed') {
                const detailedData = filteredRecords.map(record => ({
                    'Ticket Number': record.ticketNumber || '',
                    'BOL Number': record.bolNumber || '',
                    'Delivery Date': record.deliveryDate || '',
                    'Carrier': record.carrier || '',
                    'Supplier': record.supplier || '',
                    'Outlet': record.outlet || '',
                    'Net Tons': record.netTons || 0,
                    'Gross Price': record.grossPrice || 0,
                    'Freight Cost': record.freightCost || 0,
                    'Driver': record.driver || '',
                    'Paid Status': record.paidStatus ? 'Paid' : 'Unpaid',
                    'Confidence Score': record.confidenceScore || 0,
                }));
                exportToCSV(detailedData, `detailed-records-${timestamp}`);
            } else if (selectedReportType === 'company-performance') {
                exportToCSV(reportData?.topCarriers || [], `top-carriers-${timestamp}`);
            }
        }

        setExportDialogOpen(false);
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress size={60} />
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{ mt: 4 }}>
                <Alert severity="error" action={
                    <IconButton color="inherit" size="small" onClick={fetchData}>
                        <Refresh />
                    </IconButton>
                }>
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="xl" sx={{ mt: 3, mb: 3 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" component="h1" fontWeight="bold">
                    Business Reports
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                    <Button
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={() => setPreviewDialogOpen(true)}
                    >
                        Preview
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={() => setExportDialogOpen(true)}
                    >
                        Export Data
                    </Button>
                    <IconButton onClick={fetchData} size="small">
                        <Refresh />
                    </IconButton>
                </Box>
            </Box>

            {/* Date Range Filter */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
                        <Box display="flex" alignItems="center" gap={1}>
                            <DateRange color="primary" />
                            <Typography variant="h6">Reporting Period</Typography>
                        </Box>
                        <DatePicker
                            label="From Date"
                            value={dateFrom}
                            onChange={(newValue) => setDateFrom(newValue)}
                            slotProps={{ textField: { size: 'small' } }}
                        />
                        <DatePicker
                            label="To Date"
                            value={dateTo}
                            onChange={(newValue) => setDateTo(newValue)}
                            slotProps={{ textField: { size: 'small' } }}
                        />
                        <Box sx={{ ml: 2 }}>
                            <Typography variant="body2" color="text.secondary">
                                {filteredRecords.length} records found
                            </Typography>
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Report Tabs */}
            <Card>
                <CardContent sx={{ p: 0 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tab icon={<Receipt />} label="Summary Report" />
                        <Tab icon={<Business />} label="Carrier Analysis" />
                        <Tab icon={<Analytics />} label="Monthly Breakdown" />
                        <Tab icon={<TableChart />} label="Detailed Records" />
                    </Tabs>

                    {/* Summary Report Tab */}
                    <TabPanel value={tabValue} index={0}>
                        <Stack spacing={3}>
                            {/* Executive Summary */}
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                                    Executive Summary
                                </Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Period: {dateFrom?.format('MMM DD, YYYY')} - {dateTo?.format('MMM DD, YYYY')}
                                </Typography>

                                <Grid container spacing={3} sx={{ mt: 1 }}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">Financial Overview</Typography>
                                        <Stack spacing={1}>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">Total Revenue:</Typography>
                                                <Typography variant="body2" fontWeight="bold">{formatCurrency(reportData?.totalRevenue || 0)}</Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">Total Freight Costs:</Typography>
                                                <Typography variant="body2" fontWeight="bold">{formatCurrency(reportData?.totalFreightCosts || 0)}</Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">Net Margin:</Typography>
                                                <Typography variant="body2" fontWeight="bold">
                                                    {formatCurrency((reportData?.totalRevenue || 0) - (reportData?.totalFreightCosts || 0))}
                                                </Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">Revenue per Ton:</Typography>
                                                <Typography variant="body2" fontWeight="bold">{formatCurrency(reportData?.avgRevenuePerTon || 0)}</Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>

                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">Operational Overview</Typography>
                                        <Stack spacing={1}>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">Total Records:</Typography>
                                                <Typography variant="body2" fontWeight="bold">{reportData?.totalRecords || 0}</Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">Total Tonnage:</Typography>
                                                <Typography variant="body2" fontWeight="bold">{formatTonnage(reportData?.totalTonnage || 0)} tons</Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">Payment Status:</Typography>
                                                <Typography variant="body2">
                                                    {reportData?.paidRecords || 0} paid, {reportData?.unpaidRecords || 0} unpaid
                                                </Typography>
                                            </Box>
                                            <Box display="flex" justifyContent="space-between">
                                                <Typography variant="body2">Active Carriers:</Typography>
                                                <Typography variant="body2" fontWeight="bold">{reportData?.topCarriers.length || 0}</Typography>
                                            </Box>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Key Insights */}
                            <Paper sx={{ p: 3 }}>
                                <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                                    Key Insights
                                </Typography>
                                <Stack spacing={2}>
                                    <Typography variant="body2">
                                        • Top performing carrier: <strong>{reportData?.topCarriers[0]?.name || 'N/A'}</strong> with {formatCurrency(reportData?.topCarriers[0]?.revenue || 0)} in revenue
                                    </Typography>
                                    <Typography variant="body2">
                                        • Highest volume carrier: <strong>{reportData?.carriersByVolume[0]?.name || 'N/A'}</strong> with {formatTonnage(reportData?.carriersByVolume[0]?.tonnage || 0)} tons
                                    </Typography>
                                    <Typography variant="body2">
                                        • Payment completion rate: <strong>{reportData?.totalRecords ? ((reportData.paidRecords / reportData.totalRecords) * 100).toFixed(1) : 0}%</strong>
                                    </Typography>
                                    <Typography variant="body2">
                                        • Average load size: <strong>{reportData?.totalRecords ? formatTonnage((reportData.totalTonnage || 0) / reportData.totalRecords) : 0} tons per load</strong>
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Stack>
                    </TabPanel>

                    {/* Carrier Analysis Tab */}
                    <TabPanel value={tabValue} index={1}>
                        <Stack spacing={3}>
                            {/* Top Carriers by Revenue */}
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                                    Carriers Ranked by Revenue
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Rank</TableCell>
                                                <TableCell>Carrier Name</TableCell>
                                                <TableCell align="right">Total Revenue</TableCell>
                                                <TableCell align="right">Total Tonnage</TableCell>
                                                <TableCell align="right">Load Count</TableCell>
                                                <TableCell align="right">Avg Revenue/Load</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData?.topCarriers.map((carrier, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{carrier.name}</TableCell>
                                                    <TableCell align="right">{formatCurrency(carrier.revenue)}</TableCell>
                                                    <TableCell align="right">{formatTonnage(carrier.tonnage)}</TableCell>
                                                    <TableCell align="right">{carrier.recordCount}</TableCell>
                                                    <TableCell align="right">
                                                        {formatCurrency(carrier.recordCount > 0 ? carrier.revenue / carrier.recordCount : 0)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>

                            {/* Top Suppliers */}
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                                    Suppliers Ranked by Revenue
                                </Typography>
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Rank</TableCell>
                                                <TableCell>Supplier Name</TableCell>
                                                <TableCell align="right">Total Revenue</TableCell>
                                                <TableCell align="right">Total Tonnage</TableCell>
                                                <TableCell align="right">Load Count</TableCell>
                                                <TableCell align="right">Avg Tonnage/Load</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {reportData?.topSuppliers.map((supplier, index) => (
                                                <TableRow key={index}>
                                                    <TableCell>{index + 1}</TableCell>
                                                    <TableCell>{supplier.name}</TableCell>
                                                    <TableCell align="right">{formatCurrency(supplier.revenue)}</TableCell>
                                                    <TableCell align="right">{formatTonnage(supplier.tonnage)}</TableCell>
                                                    <TableCell align="right">{supplier.recordCount}</TableCell>
                                                    <TableCell align="right">
                                                        {formatTonnage(supplier.recordCount > 0 ? supplier.tonnage / supplier.recordCount : 0)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Stack>
                    </TabPanel>

                    {/* Monthly Breakdown Tab */}
                    <TabPanel value={tabValue} index={2}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                                Monthly Performance Analysis
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Month</TableCell>
                                            <TableCell align="right">Revenue</TableCell>
                                            <TableCell align="right">Tonnage</TableCell>
                                            <TableCell align="right">Load Count</TableCell>
                                            <TableCell align="right">Avg Revenue/Load</TableCell>
                                            <TableCell align="right">Avg Revenue/Ton</TableCell>
                                            <TableCell align="right">Avg Tonnage/Load</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData?.recordsByMonth.map((month, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{month.month}</TableCell>
                                                <TableCell align="right">{formatCurrency(month.revenue)}</TableCell>
                                                <TableCell align="right">{formatTonnage(month.tonnage)}</TableCell>
                                                <TableCell align="right">{month.recordCount}</TableCell>
                                                <TableCell align="right">
                                                    {formatCurrency(month.recordCount > 0 ? month.revenue / month.recordCount : 0)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatCurrency(month.tonnage > 0 ? month.revenue / month.tonnage : 0)}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {formatTonnage(month.recordCount > 0 ? month.tonnage / month.recordCount : 0)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </TabPanel>

                    {/* Detailed Records Tab */}
                    <TabPanel value={tabValue} index={3}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                                Detailed Transaction Records
                            </Typography>
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Ticket #</TableCell>
                                            <TableCell>BOL #</TableCell>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Carrier</TableCell>
                                            <TableCell>Supplier</TableCell>
                                            <TableCell>Driver</TableCell>
                                            <TableCell align="right">Tonnage</TableCell>
                                            <TableCell align="right">Revenue</TableCell>
                                            <TableCell align="right">Freight Cost</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="right">Confidence</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredRecords
                                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                            .map((record) => (
                                                <TableRow key={record.id}>
                                                    <TableCell>{record.ticketNumber || '-'}</TableCell>
                                                    <TableCell>{record.bolNumber || '-'}</TableCell>
                                                    <TableCell>
                                                        {record.deliveryDate ?
                                                            new Date(record.deliveryDate).toLocaleDateString() :
                                                            '-'
                                                        }
                                                    </TableCell>
                                                    <TableCell>{record.carrier || '-'}</TableCell>
                                                    <TableCell>{record.supplier || '-'}</TableCell>
                                                    <TableCell>{record.driver || '-'}</TableCell>
                                                    <TableCell align="right">
                                                        {record.netTons ? formatTonnage(record.netTons) : '-'}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {record.grossPrice ? formatCurrency(record.grossPrice) : '-'}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {record.freightCost ? formatCurrency(record.freightCost) : '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={record.paidStatus ? 'Paid' : 'Unpaid'}
                                                            color={record.paidStatus ? 'success' : 'default'}
                                                            size="small"
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {record.confidenceScore ?
                                                            `${(record.confidenceScore * 100).toFixed(0)}%` :
                                                            '-'
                                                        }
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                component="div"
                                count={filteredRecords.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </Paper>
                    </TabPanel>
                </CardContent>
            </Card>

            {/* Export Dialog */}
            <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Export Report Data</DialogTitle>
                <DialogContent>
                    <Box sx={{ mb: 3 }}>
                        <FormControl fullWidth sx={{ mb: 2 }}>
                            <InputLabel>Report Type</InputLabel>
                            <Select
                                value={selectedReportType}
                                label="Report Type"
                                onChange={(e) => setSelectedReportType(e.target.value)}
                            >
                                <MenuItem value="financial">Financial Summary</MenuItem>
                                <MenuItem value="detailed">Detailed Records</MenuItem>
                                <MenuItem value="company-performance">Company Performance</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Typography variant="subtitle2" gutterBottom>Available formats:</Typography>
                    <List>
                        <ListItem
                            onClick={() => exportCurrentReport('csv')}
                            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        >
                            <ListItemIcon>
                                <TableChart color="primary" />
                            </ListItemIcon>
                            <ListItemText
                                primary="CSV Export"
                                secondary="Export data as comma-separated values for Excel or other spreadsheet applications"
                            />
                        </ListItem>
                        <ListItem
                            sx={{ opacity: 0.6, cursor: 'not-allowed' }}
                        >
                            <ListItemIcon>
                                <PictureAsPdf color="action" />
                            </ListItemIcon>
                            <ListItemText
                                primary="PDF Report"
                                secondary="Professional formatted report (Coming Soon)"
                            />
                        </ListItem>
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* Preview Dialog */}
            <Dialog open={previewDialogOpen} onClose={() => setPreviewDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>Report Preview</DialogTitle>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>Business Report Summary</Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Reporting Period: {dateFrom?.format('MMM DD, YYYY')} - {dateTo?.format('MMM DD, YYYY')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Generated on: {new Date().toLocaleDateString()}
                    </Typography>
                    <Divider sx={{ my: 2 }} />

                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">Financial Overview</Typography>
                            <Typography variant="body2">Total Revenue: {formatCurrency(reportData?.totalRevenue || 0)}</Typography>
                            <Typography variant="body2">Total Freight Costs: {formatCurrency(reportData?.totalFreightCosts || 0)}</Typography>
                            <Typography variant="body2">Net Margin: {formatCurrency((reportData?.totalRevenue || 0) - (reportData?.totalFreightCosts || 0))}</Typography>
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">Operational Summary</Typography>
                            <Typography variant="body2">Total Records: {reportData?.totalRecords || 0}</Typography>
                            <Typography variant="body2">Total Tonnage: {formatTonnage(reportData?.totalTonnage || 0)} tons</Typography>
                            <Typography variant="body2">Active Carriers: {reportData?.topCarriers.length || 0}</Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button startIcon={<Print />} onClick={() => window.print()}>
                        Print Preview
                    </Button>
                    <Button onClick={() => setPreviewDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Reports;
